import { useEffect } from 'react';

import { metaDescription, pageTitle } from '../../shared/meta.mjs';

/**
 * Keeps the document head describing the page you are actually on.
 *
 * The server writes the right tags into the HTML for the first load — that is what crawlers and
 * share-card scrapers read, and they never get further than that. But a visitor clicking from the
 * front page to /program never asks the server for anything: React Router swaps the page and the
 * head is left describing the front page. This is the client's half of the same job.
 *
 * One hook for every page, rather than each page reaching into `document` on its own, so the title
 * format lives in one place (`shared/meta.mjs`, the same module the server formats with) and
 * nothing can leave a tag behind when it unmounts.
 *
 * Pass `null` while the page is still loading: nothing is touched until there is something true to
 * say, so the title the server wrote survives until the client has a better one.
 */
export interface PageMeta {
  /** The page's own name — "Program". Formatted into "Program – Midtfyns Festival" here. */
  readonly title?: string | null;
  /** Plain text or HTML; it is reduced to one short line either way. */
  readonly description?: string | null;
  /** Keep this page out of search results (a preview, an unlisted page, a 404). */
  readonly noIndex?: boolean;
  /** The front page keeps the tagline instead of taking the suffix. */
  readonly isHome?: boolean;
}

/** Finds the tag, or creates it, so a page can set something the shell never had. */
function head(selector: string, create: () => HTMLElement): HTMLElement {
  const existing = document.head.querySelector<HTMLElement>(selector);
  if (existing !== null) return existing;
  const element = create();
  document.head.appendChild(element);
  return element;
}

function metaTag(name: string): HTMLMetaElement {
  return head(`meta[name="${name}"]`, () => {
    const element = document.createElement('meta');
    element.setAttribute('name', name);
    return element;
  }) as HTMLMetaElement;
}

export function usePageMeta({ title, description, noIndex = false, isHome = false }: PageMeta): void {
  useEffect(() => {
    if (title === null || title === undefined) return;

    const formatted = pageTitle(title, { isHome });
    const previousTitle = document.title;
    document.title = formatted;

    const descriptionTag = metaTag('description');
    const previousDescription = descriptionTag.content;
    if (description) descriptionTag.content = metaDescription(description);

    // The canonical is written by the server for the address that was requested; on an in-app
    // navigation it has to follow along, or a visitor who shares the URL of the page they browsed
    // to hands out a link that points somewhere else.
    const canonical = head('link[rel="canonical"]', () => {
      const element = document.createElement('link');
      element.setAttribute('rel', 'canonical');
      return element;
    }) as HTMLLinkElement;
    const previousCanonical = canonical.href;
    // Keep the origin the server wrote. On production it is the canonical host either way, but on
    // any other hostname — a deploy check on *.azurewebsites.net — rewriting it to the current
    // origin would undo the one thing the tag is for.
    const origin = previousCanonical === '' ? window.location.origin : new URL(previousCanonical).origin;
    canonical.href = origin + window.location.pathname;

    // Only ever added, never flipped to "index": a page that should be indexed says so by having
    // no robots tag at all, which is what the shell ships with.
    const robots = noIndex ? metaTag('robots') : null;
    const previousRobots = robots?.content ?? '';
    if (robots !== null) robots.content = 'noindex,nofollow';

    return () => {
      document.title = previousTitle;
      descriptionTag.content = previousDescription;
      canonical.href = previousCanonical;
      if (robots !== null) {
        if (previousRobots === '') robots.remove();
        else robots.content = previousRobots;
      }
    };
  }, [title, description, noIndex, isHome]);
}
